import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, Users, TrendingUp, PieChart, Mail } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
export function Investors() {
  const {
    t
  } = useLanguage();
  useEffect(() => {
    setPageMeta({
      title: 'Investors',
      description: 'Own a piece of Dance for Fans. 49% equity is on the table — explore the model, the math, and the upside.'
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0a1f12',
    color: '#e8f5e9'
  }} data-id="element-337">
      {/* Money-green ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-338">
        <motion.div className="absolute top-[-20%] left-[10%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(34, 197, 94, 0.12)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-339" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(16, 185, 129, 0.15)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-340" />
      </div>

      <div className="relative z-10" data-id="element-341">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(232, 245, 233, 0.1)'
      }} data-id="element-342">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-343">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#e8f5e9'
          }} data-id="element-344">
              <ArrowLeft className="w-4 h-4" data-id="element-345" />
              {t('common.backToMain')}
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: '#86efac'
          }} data-id="element-346">
              {t('investorsPage.headerTag')}
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-24 md:py-28" data-id="element-347">
          <div className="max-w-4xl mx-auto text-center" data-id="element-348">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-sm uppercase tracking-[0.4em] mb-6" style={{
            color: '#86efac'
          }} data-id="element-349">
              {t('investorsPage.heroTag')}
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif italic text-5xl md:text-7xl lg:text-8xl leading-[1.05] mb-8" style={{
            color: '#e8f5e9'
          }} data-id="element-350">
              {t('investorsPage.heroTitleStart')}
              <span style={{
              color: '#22c55e'
            }} data-id="element-351">
                {t('investorsPage.heroTitleHighlight')}
              </span>
              {t('investorsPage.heroTitleEnd')}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{
            color: 'rgba(232, 245, 233, 0.75)'
          }} data-id="element-352">
              {t('investorsPage.p1')}
            </motion.p>
          </div>
        </section>

        {/* Two paths */}
        <section className="px-6 pb-12" data-id="element-353">
          <div className="max-w-5xl mx-auto" data-id="element-354">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="text-center mb-10" data-id="element-355">
              <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{
              color: '#e8f5e9'
            }} data-id="element-356">
                {t('investorsPage.twoPathsTitle')}
              </h2>
              <p className="text-base md:text-lg max-w-2xl mx-auto" style={{
              color: 'rgba(232, 245, 233, 0.7)'
            }} data-id="element-357">
                {t('investorsPage.p2')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-358">
              {/* Option 1 */}
              <motion.article initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="rounded-2xl p-8 border backdrop-blur-sm" style={{
              backgroundColor: 'rgba(15, 40, 25, 0.5)',
              borderColor: 'rgba(232, 245, 233, 0.1)'
            }} data-id="element-359">
                <div className="flex items-center gap-3 mb-6" data-id="element-360">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232, 245, 233, 0.06)'
                }} data-id="element-361">
                    <Users className="w-6 h-6" style={{
                    color: '#86efac'
                  }} data-id="element-362" />
                  </div>
                  <span className="text-xs uppercase tracking-widest" style={{
                  color: '#86efac'
                }} data-id="element-363">
                    {t('investorsPage.option1Tag')}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mb-3" style={{
                color: '#e8f5e9'
              }} data-id="element-364">
                  {t('investorsPage.option1Title')}
                </h3>
                <p className="leading-relaxed" style={{
                color: 'rgba(232, 245, 233, 0.7)'
              }} data-id="element-365">
                  {t('investorsPage.option1Desc')}
                </p>
              </motion.article>

              {/* Option 2 - featured */}
              <motion.article initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="rounded-2xl p-8 border backdrop-blur-sm" style={{
              backgroundColor: 'rgba(20, 60, 35, 0.6)',
              borderColor: 'rgba(34, 197, 94, 0.4)'
            }} data-id="element-366">
                <div className="flex items-center gap-3 mb-6" data-id="element-367">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.2)'
                }} data-id="element-368">
                    <Users className="w-6 h-6" style={{
                    color: '#22c55e'
                  }} data-id="element-369" />
                  </div>
                  <span className="text-xs uppercase tracking-widest" style={{
                  color: '#22c55e'
                }} data-id="element-370">
                    {t('investorsPage.option2Tag')}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mb-3" style={{
                color: '#e8f5e9'
              }} data-id="element-371">
                  {t('investorsPage.option2Title')}
                </h3>
                <p className="leading-relaxed" style={{
                color: 'rgba(232, 245, 233, 0.75)'
              }} data-id="element-372">
                  {t('investorsPage.option2Desc')}
                </p>
              </motion.article>
            </div>
          </div>
        </section>

        {/* Why option 2 */}
        <section className="px-6 py-20" data-id="element-373">
          <div className="max-w-3xl mx-auto" data-id="element-374">
            {/* Header */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="text-center mb-16" data-id="element-375">
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#86efac'
            }} data-id="element-376">
                {t('investorsPage.whyOption2Tag')}
              </p>
              <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-6" style={{
              color: '#e8f5e9'
            }} data-id="element-377">
                {t('investorsPage.whyOption2Title')}
              </h2>
              <p className="font-serif italic text-xl md:text-2xl" style={{
              color: '#86efac'
            }} data-id="element-378">
                {t('investorsPage.whyOption2Subtitle')}
              </p>
            </motion.div>

            {/* Intro */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-6 mb-16 text-base md:text-lg leading-relaxed" style={{
            color: 'rgba(232, 245, 233, 0.85)'
          }} data-id="element-379">
              <p data-id="element-380">{t('investorsPage.whyOption2Intro1')}</p>
              <p data-id="element-381">{t('investorsPage.whyOption2Intro2')}</p>
            </motion.div>

            {/* Growth subsection */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="mb-16" data-id="element-382">
              <h3 className="font-serif text-2xl md:text-4xl mb-6 leading-tight" style={{
              color: '#22c55e'
            }} data-id="element-383">
                {t('investorsPage.whyOption2GrowthHeader')}
              </h3>
              <div className="space-y-6 text-base md:text-lg leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.85)'
            }} data-id="element-384">
                <p data-id="element-385">{t('investorsPage.whyOption2GrowthP1')}</p>
                <div data-id="element-386">
                  <p className="mb-4" data-id="element-387">
                    {t('investorsPage.whyOption2GrowthLead')}
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-2" data-id="element-388">
                    {(t('investorsPage.whyOption2GrowthBullets') as string[]).map((b, i) => <li key={i} className="flex items-center gap-3 rounded-xl p-3 border" style={{
                    backgroundColor: 'rgba(15, 40, 25, 0.5)',
                    borderColor: 'rgba(34, 197, 94, 0.25)',
                    color: '#e8f5e9'
                  }} data-id="element-389">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: '#22c55e'
                    }} data-id="element-390" />
                        <span data-id="element-391">{b}</span>
                      </li>)}
                  </ul>
                </div>
                <p data-id="element-392">{t('investorsPage.whyOption2GrowthP2')}</p>
              </div>
            </motion.div>

            {/* Proven subsection */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="mb-16" data-id="element-393">
              <h3 className="font-serif text-2xl md:text-4xl mb-6 leading-tight" style={{
              color: '#22c55e'
            }} data-id="element-394">
                {t('investorsPage.whyOption2ProvenHeader')}
              </h3>
              <p className="text-base md:text-lg leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.85)'
            }} data-id="element-395">
                {t('investorsPage.whyOption2ProvenP')}
              </p>
            </motion.div>

            {/* Matters subsection - emphasized closing */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-8 md:p-12 border" style={{
            backgroundColor: 'rgba(20, 60, 35, 0.6)',
            borderColor: 'rgba(34, 197, 94, 0.4)'
          }} data-id="element-396">
              <h3 className="font-serif text-2xl md:text-4xl mb-6 leading-tight" style={{
              color: '#22c55e'
            }} data-id="element-397">
                {t('investorsPage.whyOption2MattersHeader')}
              </h3>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{
              color: 'rgba(232, 245, 233, 0.9)'
            }} data-id="element-398">
                {t('investorsPage.whyOption2MattersP1')}
              </p>
              <p className="font-serif italic text-lg md:text-xl leading-relaxed" style={{
              color: '#e8f5e9'
            }} data-id="element-399">
                {t('investorsPage.whyOption2MattersP2')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Math breakdown */}
        <section className="px-6 py-16" data-id="element-400">
          <div className="max-w-5xl mx-auto" data-id="element-401">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="text-center mb-12" data-id="element-402">
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#86efac'
            }} data-id="element-403">
                {t('investorsPage.mathTag')}
              </p>
              <h2 className="font-serif text-3xl md:text-5xl" style={{
              color: '#e8f5e9'
            }} data-id="element-404">
                {t('investorsPage.mathTitle')}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-id="element-405">
              {/* Stat: 1,000 investors */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0
            }} className="rounded-2xl p-6 border" style={{
              backgroundColor: 'rgba(15, 40, 25, 0.5)',
              borderColor: 'rgba(232, 245, 233, 0.1)'
            }} data-id="element-406">
                <Users className="w-5 h-5 mb-3" style={{
                color: '#86efac'
              }} data-id="element-407" />
                <p className="font-serif text-4xl md:text-5xl mb-2" style={{
                color: '#22c55e'
              }} data-id="element-408">
                  1,000
                </p>
                <p className="text-sm" style={{
                color: 'rgba(232, 245, 233, 0.6)'
              }} data-id="element-409">
                  {t('investorsPage.stat1')}
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.05
            }} className="rounded-2xl p-6 border" style={{
              backgroundColor: 'rgba(15, 40, 25, 0.5)',
              borderColor: 'rgba(232, 245, 233, 0.1)'
            }} data-id="element-410">
                <DollarSign className="w-5 h-5 mb-3" style={{
                color: '#86efac'
              }} data-id="element-411" />
                <p className="font-serif text-4xl md:text-5xl mb-2" style={{
                color: '#22c55e'
              }} data-id="element-412">
                  $2,500
                </p>
                <p className="text-sm" style={{
                color: 'rgba(232, 245, 233, 0.6)'
              }} data-id="element-413">
                  {t('investorsPage.stat2')}
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="rounded-2xl p-6 border" style={{
              backgroundColor: 'rgba(20, 60, 35, 0.6)',
              borderColor: 'rgba(34, 197, 94, 0.4)'
            }} data-id="element-414">
                <TrendingUp className="w-5 h-5 mb-3" style={{
                color: '#22c55e'
              }} data-id="element-415" />
                <p className="font-serif text-4xl md:text-5xl mb-2" style={{
                color: '#22c55e'
              }} data-id="element-416">
                  $2.5M
                </p>
                <p className="text-sm" style={{
                color: 'rgba(232, 245, 233, 0.6)'
              }} data-id="element-417">
                  {t('investorsPage.stat3')}
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.15
            }} className="rounded-2xl p-6 border" style={{
              backgroundColor: 'rgba(20, 60, 35, 0.6)',
              borderColor: 'rgba(34, 197, 94, 0.4)'
            }} data-id="element-418">
                <PieChart className="w-5 h-5 mb-3" style={{
                color: '#22c55e'
              }} data-id="element-419" />
                <p className="font-serif text-4xl md:text-5xl mb-2" style={{
                color: '#22c55e'
              }} data-id="element-420">
                  49%
                </p>
                <p className="text-sm" style={{
                color: 'rgba(232, 245, 233, 0.6)'
              }} data-id="element-421">
                  {t('investorsPage.stat4')}
                </p>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-8 md:p-10 border-l-2 max-w-3xl mx-auto" style={{
            borderColor: '#22c55e',
            backgroundColor: 'rgba(15, 40, 25, 0.4)'
          }} data-id="element-422">
              <p className="font-serif text-xl md:text-2xl leading-relaxed mb-4" style={{
              color: '#e8f5e9'
            }} data-id="element-423">
                {t('investorsPage.p3')}
              </p>
              <p className="text-base md:text-lg" style={{
              color: 'rgba(232, 245, 233, 0.7)'
            }} data-id="element-424">
                {t('investorsPage.p4Start')}
                <span className="font-serif" style={{
                color: '#22c55e'
              }} data-id="element-425">
                  {t('investorsPage.p4Highlight')}
                </span>
                {t('investorsPage.p4End')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Upside projection */}
        <section className="px-6 py-16" data-id="element-426">
          <div className="max-w-4xl mx-auto" data-id="element-427">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-10 md:p-14 border" style={{
            backgroundColor: 'rgba(20, 60, 35, 0.6)',
            borderColor: 'rgba(134, 239, 172, 0.3)'
          }} data-id="element-428">
              <div className="text-center mb-10" data-id="element-429">
                <TrendingUp className="w-8 h-8 mx-auto mb-4" style={{
                color: '#86efac'
              }} data-id="element-430" />
                <p className="text-xs uppercase tracking-[0.4em] mb-3" style={{
                color: '#86efac'
              }} data-id="element-431">
                  {t('investorsPage.upsideTag')}
                </p>
                <h2 className="font-serif italic text-3xl md:text-4xl leading-snug" style={{
                color: '#e8f5e9'
              }} data-id="element-432">
                  {t('investorsPage.upsideTitle')}
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-id="element-433">
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-434">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-435">
                    {t('investorsPage.projMonthlyRevLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-436">
                    $20M
                  </p>
                </div>
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-437">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-438">
                    {t('investorsPage.projInvestorShareLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-439">
                    $9.8M
                  </p>
                </div>
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-440">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-441">
                    {t('investorsPage.projPerInvestorLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-442">
                    ~$9,800
                  </p>
                  <p className="text-xs mt-2" style={{
                  color: 'rgba(232, 245, 233, 0.5)'
                }} data-id="element-443">
                    {t('investorsPage.projPerInvestorSub')}
                  </p>
                </div>
              </div>

              <p className="text-sm md:text-base text-center max-w-2xl mx-auto leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.65)'
            }} data-id="element-444">
                {t('investorsPage.p5')}
              </p>

              {/* Bigger upside tier */}
              <div className="my-12 h-px max-w-md mx-auto" style={{
              backgroundColor: 'rgba(34, 197, 94, 0.25)'
            }} data-id="element-445" />

              <p className="text-xs uppercase tracking-[0.4em] mb-6 text-center" style={{
              color: '#86efac'
            }} data-id="element-446">
                {t('investorsPage.bigUpsideTag')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-id="element-447">
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-448">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-449">
                    {t('investorsPage.projMonthlyRevLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-450">
                    $166M
                  </p>
                </div>
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-451">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-452">
                    {t('investorsPage.projInvestorShareLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-453">
                    $81.3M
                  </p>
                </div>
                <div className="rounded-2xl p-6 text-center border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-454">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-455">
                    {t('investorsPage.projPerInvestorLabel')}
                  </p>
                  <p className="font-serif text-3xl md:text-4xl" style={{
                  color: '#22c55e'
                }} data-id="element-456">
                    ~$81,300
                  </p>
                  <p className="text-xs mt-2" style={{
                  color: 'rgba(232, 245, 233, 0.5)'
                }} data-id="element-457">
                    {t('investorsPage.projPerInvestorSub')}
                  </p>
                </div>
              </div>

              <p className="text-sm md:text-base text-center max-w-2xl mx-auto leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.65)'
            }} data-id="element-458">
                {t('investorsPage.bigUpsideP')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Disclaimer + CTA */}
        <section className="px-6 py-12" data-id="element-459">
          <div className="max-w-3xl mx-auto text-center" data-id="element-460">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-461">
              <p className="text-xs mb-8 max-w-xl mx-auto leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.4)'
            }} data-id="element-462">
                {t('investorsPage.disclaimer')}
              </p>
              <a href="mailto:jose@danceforfans.com" className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-medium transition-colors" style={{
              backgroundColor: '#22c55e',
              color: '#0a1f12'
            }} data-id="element-463">
                <Mail className="w-5 h-5" data-id="element-464" />
                {t('investorsPage.contactBtn')}
              </a>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(232, 245, 233, 0.1)'
      }} data-id="element-465">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" data-id="element-466">
            <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{
            color: '#e8f5e9'
          }} data-id="element-467">
              <ArrowLeft className="w-4 h-4" data-id="element-468" />
              {t('common.backToMainStage')}
            </Link>
            <span style={{
            color: 'rgba(232, 245, 233, 0.5)'
          }} data-id="element-469">
              {t('investorsPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}